import React, { useState } from 'react';
import { Lock, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

export const SetPasswordScreen = () => {
  const { user, token, markPasswordSet, logout } = useAuth();
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (pw.length < 8 || !/[A-Za-z]/.test(pw) || !/[0-9]/.test(pw)) {
      setError('Password must be at least 8 characters and include a letter and a number.');
      return;
    }
    if (pw !== confirm) { setError('The two passwords do not match.'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/auth/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token || localStorage.getItem('dentalToken')}` },
        body: JSON.stringify({ newPassword: pw }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Could not save your password. Please try again.');
      markPasswordSet();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-emerald-50 flex items-center justify-center p-4" data-testid="set-password-screen">
      <Card className="w-full max-w-md shadow-xl">
        <CardContent className="pt-10 pb-8 px-8">
          <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 text-center mb-1">Create your password</h1>
          <p className="text-gray-600 text-center text-sm mb-6">
            Welcome{user?.firstName ? `, ${user.firstName}` : ''}! Replace your temporary password with one only you know.
          </p>
          <form onSubmit={submit} className="space-y-4">
            <div className="relative">
              <Lock className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input type={show ? 'text' : 'password'} placeholder="New password" value={pw} onChange={(e) => setPw(e.target.value)}
                className="pl-9 pr-10 h-12" autoComplete="new-password" data-testid="set-password-new-input" />
              <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                aria-label="Show password" data-testid="set-password-toggle-visibility">
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <div className="relative">
              <Lock className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input type={show ? 'text' : 'password'} placeholder="Confirm new password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                className="pl-9 h-12" autoComplete="new-password" data-testid="set-password-confirm-input" />
            </div>
            <p className="text-xs text-gray-500">At least 8 characters, with at least one letter and one number.</p>
            {error && <p className="text-sm text-red-600" data-testid="set-password-error">{error}</p>}
            <Button type="submit" disabled={saving} className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold" data-testid="set-password-submit-btn">
              {saving ? 'Saving...' : 'Save password & continue'}
            </Button>
          </form>
          <button onClick={logout} className="block mx-auto mt-4 text-xs text-gray-500 underline" data-testid="set-password-logout-btn">Log out</button>
        </CardContent>
      </Card>
    </div>
  );
};
